import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", company_name: "", password: "", confirm_password: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm_password) {
      setErr("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      navigate(`/verify-email?email=${encodeURIComponent(form.email)}${data.demo_mode ? "&demo=1" : ""}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
      <form className="w-full max-w-sm space-y-5" onSubmit={submit} data-testid="register-form">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Get Started</div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Create your account</h2>
          <p className="text-xs text-zinc-500 mt-2">
            You&apos;ll receive a 6-digit code to verify your work email before signing in.
          </p>
        </div>

        {err && (
          <div className="text-sm text-red-600 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-sm" data-testid="register-error">
            {err}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Full name</Label>
          <Input data-testid="register-name-input" value={form.name} onChange={change("name")} required className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Work email</Label>
          <Input data-testid="register-email-input" type="email" value={form.email} onChange={change("email")} required className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Company name</Label>
          <Input data-testid="register-company-input" value={form.company_name} onChange={change("company_name")} required className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Password</Label>
          <Input data-testid="register-password-input" type="password" value={form.password} onChange={change("password")} required minLength={8} className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Confirm password</Label>
          <Input data-testid="register-confirm-password-input" type="password" value={form.confirm_password} onChange={change("confirm_password")} required minLength={8} className="rounded-sm" />
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="register-submit-button">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-sm text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="link-login">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
